import {useEffect, useState} from 'react';
import {X, Check, AlertCircle} from 'lucide-react';
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
  ScrollArea,
  Spinner,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useJobShares,
  useFolderShares,
  useCreateJobShare,
  useCreateFolderShare,
  useDeleteJobShare,
  useDeleteFolderShare,
  useAnalystSearch
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly kind: 'job' | 'folder';
  readonly entityId: string;
  readonly entityLabel: string;
};

export function ShareDialog({open, onClose, kind, entityId, entityLabel}: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [permission, setPermission] = useState<'view' | 'act'>('view');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const jobShares = useJobShares(entityId, open && kind === 'job');
  const folderShares = useFolderShares(entityId, open && kind === 'folder');
  const sharesQuery = kind === 'job' ? jobShares : folderShares;

  const createJobShare = useCreateJobShare();
  const createFolderShare = useCreateFolderShare();
  const deleteJobShare = useDeleteJobShare();
  const deleteFolderShare = useDeleteFolderShare();

  const {data: analysts} = useAnalystSearch(debouncedSearch || undefined);
  const shares = sharesQuery.data ?? [];
  const alreadySharedEmails = new Set(shares.map((s) => s.shared_with_email));

  const creating = kind === 'job' ? createJobShare.isPending : createFolderShare.isPending;

  const shareWith = async (email: string) => {
    try {
      setError(null);
      await (kind === 'job'
        ? createJobShare.mutateAsync({jobId: entityId, body: {email, permission}})
        : createFolderShare.mutateAsync({folderId: entityId, body: {email, permission}}));
      setSearch('');
    } catch (error_: unknown) {
      setError(getApiErrorMessage(error_, 'Failed to share'));
    }
  };

  const revoke = async (shareId: string) => {
    try {
      setError(null);
      await (kind === 'job'
        ? deleteJobShare.mutateAsync({jobId: entityId, shareId})
        : deleteFolderShare.mutateAsync({folderId: entityId, shareId}));
    } catch (error_: unknown) {
      setError(getApiErrorMessage(error_, 'Failed to revoke access'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share {kind === 'job' ? 'investigation' : 'folder'}</DialogTitle>
          <DialogDescription className="truncate">{entityLabel}</DialogDescription>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}

        <FieldGroup className="gap-4">
          <Field>
            <FieldLabel htmlFor="share-analyst-search">Add an analyst</FieldLabel>
            <div className="flex items-center gap-1.5">
              <Input
                autoFocus
                id="share-analyst-search"
                placeholder="Search analysts by name or email…"
                value={search}
                className="min-w-0 flex-1"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
              <ToggleGroup
                variant="outline"
                spacing={0}
                size="sm"
                aria-label="Permission"
                className="shrink-0"
                value={[permission]}
                onValueChange={(next) => {
                  const nextPermission = next[0];
                  if (nextPermission === 'view' || nextPermission === 'act') {
                    setPermission(nextPermission);
                  }
                }}>
                <ToggleGroupItem value="view">View</ToggleGroupItem>
                <ToggleGroupItem value="act">Can act</ToggleGroupItem>
              </ToggleGroup>
            </div>

            {debouncedSearch && analysts && analysts.length > 0 ? (
              // The viewport is `overflow: scroll`, so as a flex item its minimum height is 0 and
              // it shrinks to the max-height rather than growing past it.
              <ScrollArea className="border-border flex max-h-40 flex-col rounded-md border">
                {analysts.map((a) => {
                  const alreadyShared = alreadySharedEmails.has(a.email);
                  return (
                    <Button
                      key={a.email}
                      variant="ghost"
                      disabled={alreadyShared || creating}
                      className="h-auto w-full justify-between px-3 py-2 font-normal"
                      onClick={() => void shareWith(a.email)}>
                      <span className="min-w-0 truncate">
                        <span className="font-medium">{a.display_name}</span>{' '}
                        <span className="text-muted-foreground">{a.email}</span>
                      </span>
                      {alreadyShared ? <Check className="text-muted-foreground" /> : null}
                    </Button>
                  );
                })}
              </ScrollArea>
            ) : null}
          </Field>

          <Field>
            <FieldLabel>Shared with {shares.length > 0 ? `(${shares.length})` : ''}</FieldLabel>
            {sharesQuery.isLoading ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : shares.length === 0 ? (
              <FieldDescription className="italic">Not shared with anyone yet.</FieldDescription>
            ) : (
              <div className="space-y-1">
                {shares.map((s) => (
                  <Item key={s.id} variant="outline" size="xs">
                    <ItemContent>
                      <ItemTitle className="block w-full truncate font-normal">
                        {s.shared_with_email}
                      </ItemTitle>
                    </ItemContent>
                    <ItemActions>
                      <Badge variant="outline" className="uppercase">
                        {s.permission}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Revoke access for ${s.shared_with_email}`}
                        className="text-muted-foreground hover:text-destructive-ink"
                        onClick={() => void revoke(s.id)}>
                        <X />
                      </Button>
                    </ItemActions>
                  </Item>
                ))}
              </div>
            )}
          </Field>

          <Button variant="secondary" className="w-full" onClick={onClose}>
            Done
          </Button>
        </FieldGroup>
      </DialogContent>
    </Dialog>
  );
}
